import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Loader2, Clock, Lock, CheckCircle2, Home, KeyRound, ShieldAlert } from 'lucide-react';

/* ── Live countdown hook ─────────────────────────────────────── */
function useCountdown(targetDate) {
  const [diff, setDiff] = useState(() => targetDate - Date.now());
  useEffect(() => {
    const id = setInterval(() => setDiff(targetDate - Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  const total = Math.max(0, diff);
  return {
    days:    Math.floor(total / 86400000),
    hours:   Math.floor((total % 86400000) / 3600000),
    minutes: Math.floor((total % 3600000) / 60000),
    seconds: Math.floor((total % 60000) / 1000),
    expired: diff <= 0,
  };
}

function TimerBlock({ label, value }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-3xl font-extrabold font-mono text-black w-14 text-center">
        {String(value).padStart(2, '0')}
      </span>
      <span className="text-xs text-gray-500 mt-1">{label}</span>
    </div>
  );
}
const Sep = () => <span className="text-2xl font-bold text-gray-300 pb-4">:</span>;

/* ── Lockout countdown ──────────────────────────────────────── */
function useLockoutCountdown(lockedUntil) {
  const [secs, setSecs] = useState(() => Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000)));
  useEffect(() => {
    if (!lockedUntil) return;
    const id = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setSecs(remaining);
      if (remaining <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);
  return secs;
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [examLoading, setExamLoading] = useState(true);
  const [hallTicket, setHallTicket] = useState('');
  const [password, setPassword] = useState('');
  const [examCode, setExamCode] = useState('');
  const [examSettings, setExamSettings] = useState(null);

  /* attempt tracking */
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [lockedUntil, setLockedUntil] = useState(0);
  const lockSecs = useLockoutCountdown(lockedUntil);
  const isLocked = lockSecs > 0;

  const { loginStudent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/exam-settings')
      .then(({ data }) => setExamSettings(data.data || null))
      .catch(() => {})
      .finally(() => setExamLoading(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLocked) return;
    if (!hallTicket || !password || !examCode) return toast.error('Please fill all fields including the exam code');

    setLoading(true);
    try {
      await loginStudent(hallTicket.toUpperCase(), password, examCode.toUpperCase().trim());
      toast.success('Welcome! 🎓');
      navigate('/student/dashboard');
    } catch (err) {
      const data = err.response?.data;
      const msg  = data?.message || 'Login failed';
      toast.error(msg);

      if (err.response?.status === 429 && data?.lockedUntil) {
        setLockedUntil(data.lockedUntil);
        setAttemptsLeft(0);
      } else if (data?.attemptsLeft !== undefined) {
        setAttemptsLeft(data.attemptsLeft);
        if (data.attemptsLeft <= 0 && data?.lockedUntil) {
          setLockedUntil(data.lockedUntil);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  /* exam window */
  const now = new Date();
  const startTime = examSettings?.startTime ? new Date(examSettings.startTime) : null;
  const endTime   = examSettings?.endTime   ? new Date(examSettings.endTime)   : null;

  let windowStatus = 'open';
  if (!startTime && !endTime)          windowStatus = 'no-schedule';
  else if (startTime && now < startTime) windowStatus = 'upcoming';
  else if (endTime   && now > endTime)   windowStatus = 'ended';

  const countdown = useCountdown(startTime ? startTime.getTime() : 0);
  const formDisabled = windowStatus === 'ended' || isLocked;

  /* attempt indicator dots */
  const AttemptDots = () => (
    <div className="flex items-center gap-1.5 justify-center mt-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className={`w-2 h-2 rounded-full transition-all duration-300 ${
          i < attemptsLeft ? 'bg-green-500' : 'bg-red-400'
        }`} />
      ))}
      <span className="text-xs text-gray-500 ml-1">
        {isLocked ? `Locked for ${lockSecs}s` : `${attemptsLeft} attempt${attemptsLeft !== 1 ? 's' : ''} left`}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        {/* Back to Home */}
        <div className="mb-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors">
            <Home size={16} /> Back to Home
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-sm mb-4 border border-gray-200">
            <img src="/bits-logo.png" alt="BITS Logo" className="w-10 h-10 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-black">Student Exam Login</h1>
          <p className="text-gray-500 text-sm mt-1">Enter your credentials and exam code</p>
        </div>

        {/* Exam window banners */}
        {!examLoading && windowStatus === 'upcoming' && startTime && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-5 text-center">
            <Clock className="mx-auto mb-2 text-amber-500" size={28} />
            <p className="font-bold text-amber-800 text-sm mb-3">Exam starts in</p>
            <div className="flex items-end justify-center gap-2">
              {countdown.days > 0 && <><TimerBlock label="Days" value={countdown.days} /><Sep /></>}
              <TimerBlock label="Hours"   value={countdown.hours}   /><Sep />
              <TimerBlock label="Minutes" value={countdown.minutes} /><Sep />
              <TimerBlock label="Seconds" value={countdown.seconds} />
            </div>
            <p className="text-xs text-amber-600 mt-3">
              Opens at <strong>{startTime.toLocaleString('en-IN')}</strong>
            </p>
          </div>
        )}

        {!examLoading && windowStatus === 'ended' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-5 text-center">
            <Lock className="mx-auto mb-2 text-red-500" size={28} />
            <p className="font-bold text-red-800">Exam Window Has Ended</p>
            <p className="text-sm text-red-600 mt-1">This session is now closed. Contact your administrator.</p>
          </div>
        )}

        {!examLoading && windowStatus === 'open' && startTime && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 text-center">
            <CheckCircle2 className="mx-auto mb-1 text-green-500" size={20} />
            <p className="text-sm font-semibold text-green-800">
              Exam is live! Closes at {endTime?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        )}

        {/* Lockout banner */}
        {isLocked && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 flex items-center gap-3">
            <ShieldAlert className="text-red-500 shrink-0" size={24} />
            <div>
              <p className="text-sm font-bold text-red-800">Account Temporarily Locked</p>
              <p className="text-xs text-red-600 mt-0.5">Too many failed attempts. Retry in <strong>{lockSecs}s</strong>.</p>
            </div>
          </div>
        )}

        {/* Login form */}
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 transition-opacity duration-300 ${formDisabled ? 'opacity-50 pointer-events-none select-none' : ''}`}>
          <form onSubmit={handleLogin} className="space-y-4">

            {/* Hall Ticket */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hall Ticket Number</label>
              <input
                type="text" value={hallTicket}
                onChange={e => setHallTicket(e.target.value.toUpperCase())}
                placeholder="BABA2024001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono tracking-wider uppercase focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder:font-normal placeholder:tracking-normal placeholder:text-gray-400"
                autoComplete="username"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black pr-10"
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Exam Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                <KeyRound size={14} className="text-gray-500" /> Exam Code
              </label>
              <input
                type="text" value={examCode}
                onChange={e => setExamCode(e.target.value.toUpperCase())}
                placeholder=""
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono tracking-wider uppercase focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                autoComplete="off"
              />
              <p className="text-xs text-gray-400 mt-1">Exam code is provided by your administrator before the exam.</p>
            </div>

            {/* Attempts indicator */}
            {attemptsLeft < 3 && <AttemptDots />}

            <button type="submit" disabled={loading || formDisabled}
              className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-lg text-sm font-medium text-white bg-black hover:bg-gray-800 transition-colors disabled:opacity-50 mt-2">
              {loading && <Loader2 className="animate-spin" size={18} />}
              {loading ? 'Verifying...' : 'Login to Exam Portal'}
            </button>
          </form>

          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 text-center">
            Your <strong>Hall Ticket</strong>, <strong>Password</strong> and <strong>Exam Code</strong> are required to login.
          </div>
        </div>

        {/* Footer links */}
        <div className="text-center mt-5 space-y-1.5">
          <p className="text-sm text-gray-500">
            New student?{' '}
            <Link to="/student/register" className="text-black font-medium hover:underline">Register here</Link>
          </p>
          <p className="text-sm text-gray-500">
            Check status?{' '}
            <Link to="/student/status" className="text-black font-medium hover:underline">Click here</Link>
          </p>
          <p className="text-sm text-gray-500">
            Admin?{' '}
            <Link to="/admin/login" className="text-black font-medium hover:underline">Admin Login</Link>
          </p>
          <p className="text-sm text-gray-500">
            <Link to="/" className="inline-flex items-center gap-1 text-gray-400 hover:text-black transition-colors">
              <Home size={14} /> Go to Home
            </Link>
          </p>
          <p className="text-xs text-gray-400 mt-3">&copy; {new Date().getFullYear()} BITS Vizag. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
